import { User } from '@/types';

export type AdminAccess = 'allow' | 'wait' | 'deny';

// The chat store persists the signed-in user here on every update; the key is a
// bare string at its write site in store/modules/chat/actions.ts.
const STORED_USER_KEY = 'user';

/**
 * True if this browser has signed in before. Only used to decide whether an
 * unresolved user is worth waiting on, never to grant anything: a stored session
 * says who we were last time, and the server re-checks every admin event anyway.
 */
const hasStoredSession = (): boolean => {
  try {
    // Absent under the node test environment, and absent in principle if storage
    // is blocked; either way "no session" is the safe reading.
    if (typeof localStorage === 'undefined') {
      return false;
    }

    const raw = localStorage.getItem(STORED_USER_KEY);
    return raw ? (JSON.parse(raw) as Partial<User>)?.isAuthorized === true : false;
  } catch {
    return false;
  }
};

/**
 * Admin rights arrive over the socket, so on a hard refresh they are not merely
 * absent — they are not known yet, and that is a third answer. Treating it as a
 * denial bounces an admin off their own bookmark.
 *
 * That grace only extends to a browser that has signed in before. A logged-out
 * visitor typing /admin has nothing to resolve into admin rights, so they are
 * turned away immediately rather than watching the page render first.
 *
 * Resolution is a separate fact from `me` being populated. SocketConnectionLayout
 * fills `me` from localStorage in beforeMount, which lands before the admin page
 * mounts — so judging by `me.id` would decide on last session's flag and bounce an
 * admin whose stored copy predates the field or predates their promotion.
 *
 * Shared by the route guard and the admin page so the two cannot disagree about
 * who gets in.
 */
export const adminAccess = (me: User | undefined, isMeResolved: boolean): AdminAccess => {
  if (isMeResolved) {
    return me?.isWhitelistAdmin === true ? 'allow' : 'deny';
  }

  return hasStoredSession() ? 'wait' : 'deny';
};
